import { keyonFileUrl } from '@/utils/fileUrl';

/**
 * A directive record's uploaded document, read only from the uid columns keyon
 * returns (document_file_uid, has_document, document_name,
 * document_content_type). There is no path and no fallback to one: a record
 * without a uid shows "Document unavailable".
 */

/** has_document is 0|1 or a bool from keyon; the uid alone also counts. */
export function hasDocument(rec) {
  if (!rec || typeof rec !== 'object') return false;
  const flagged = rec.has_document === true || Number(rec.has_document) === 1;
  return !!(flagged || rec.document_file_uid);
}

/**
 * 'available' while the record has a document; 'unavailable' for an upload
 * without one; 'none' for a record that never had one. An upload-path record
 * always had a document (CreateDirectiveRecordUpload requires one), so an upload
 * without one was removed (DeleteDirectiveDocument) or never indexed.
 * @returns {'available'|'unavailable'|'none'}
 */
export function directiveDocumentState(rec) {
  if (hasDocument(rec)) return 'available';
  if (rec && typeof rec === 'object' && rec.ingest_path === 'upload') return 'unavailable';
  return 'none';
}

/** keyon file.php by the record's opaque uid; '' when it has none. */
export function directiveDocumentUrl(rec) {
  return keyonFileUrl(rec && rec.document_file_uid) || '';
}

export function directiveDocumentName(rec) {
  return (rec && (rec.document_name || rec.form_label)) || 'Uploaded document';
}

const EXT_BY_TYPE = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
};

/** Uppercase chip label: from the stored content type, else the stored name, else DOC. */
export function directiveDocumentExt(rec) {
  const type = String((rec && rec.document_content_type) || '').toLowerCase();
  if (EXT_BY_TYPE[type]) return EXT_BY_TYPE[type];
  const name = String((rec && rec.document_name) || '');
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toUpperCase() : '';
  return ext && ext.length <= 5 ? ext : 'DOC';
}

/** The viewers pick <img> or a PDF renderer from the row's type, never from the URL. */
export function isImageContentType(contentType) {
  return /^image\//i.test(String(contentType || ''));
}

/**
 * What the inline viewer needs to open a record, or null when the record has
 * no document. The url may be '' (flagged but no usable uid); the viewer then
 * shows "Document unavailable".
 */
export function directiveViewerFor(rec) {
  if (!hasDocument(rec)) return null;
  return {
    url: directiveDocumentUrl(rec),
    contentType: rec.document_content_type || '',
    title: rec.form_label || directiveDocumentName(rec),
    recId: rec.id,
  };
}
